// src/components/Games/PaiGowPoker/PaiGowPoker.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startPaiGow,
  resolvePaiGow,
  cancelPaiGow,
  getPaiGowHistory,
} from '../../../services/gameService';
import {
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  InputGroup,
  FormControl,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaPlay,
  FaHistory,
  FaInfoCircle,
  FaRedo,
  FaCrown,
  FaCheck,
  FaBan,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './PaiGowPoker.css';

const PaiGowPoker = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ====== Estado principal ======
  const [betAmount, setBetAmount] = useState(1);
  const [player7, setPlayer7] = useState([]);     // 7 cartas del jugador [{value,suit,card}]
  const [dealer7, setDealer7] = useState([]);     // 7 del dealer (se muestran boca abajo)
  const [assign, setAssign] = useState({});       // { 'A-H': 'low' | 'high' | 'none' }
  const [pending, setPending] = useState(false);  // sesión existente
  const [balance, setBalance] = useState(null);   // saldo reportado por el backend cuando corresponda

  // Resultado
  const [result, setResult] = useState(null);     // 'win' | 'tie' | 'lose'
  const [payout, setPayout] = useState(null);     // total devuelto (incluye ante si aplica)
  const [winnings, setWinnings] = useState(null); // ganancia neta
  const [dealerLow, setDealerLow] = useState([]); // dealer low (tras resolver) [codes]
  const [dealerHigh, setDealerHigh] = useState([]);// dealer high (tras resolver) [codes]

  // Flags UI
  const [starting, setStarting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState('');
  const [showRules, setShowRules] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [infoMsg, setInfoMsg] = useState('');

  // ====== Derivados ======
  const lowHand = useMemo(
    () => Object.entries(assign).filter(([, v]) => v === 'low').map(([code]) => code),
    [assign]
  );
  const highHand = useMemo(
    () => Object.entries(assign).filter(([, v]) => v === 'high').map(([code]) => code),
    [assign]
  );
  const canResolve = lowHand.length === 2 && highHand.length === 5;

  const inRound = useMemo(() => player7.length === 7 && !result, [player7, result]);
  const roundResolved = useMemo(() => !!result, [result]);

  const getCardImage = (code) => `${process.env.PUBLIC_URL}/images/games/cards/${code}.png`;

  // ====== Utilidades UI ======
  const resetRound = () => {
    setPlayer7([]);
    setDealer7([]);
    setAssign({});
    setPending(false);
    setResult(null);
    setPayout(null);
    setWinnings(null);
    setDealerLow([]);
    setDealerHigh([]);
    setError('');
    setInfoMsg('');
  };

  const cycleAssign = (code) => {
    // no cambiar si ya está resuelto
    if (roundResolved) return;
    setAssign((prev) => {
      const cur = prev[code] || 'none';
      // Ciclo: none -> low -> high -> none (respetando límites 2/5)
      let next = 'none';
      if (cur === 'none') next = 'low';
      else if (cur === 'low') next = 'high';
      else next = 'none';

      // Si superaríamos límite, mantener estado actual
      if (next === 'low' && lowHand.length >= 2) return prev;
      if (next === 'high' && highHand.length >= 5) return prev;

      return { ...prev, [code]: next };
    });
  };

  const clearAssignments = () => setAssign({});

  // ====== Acciones ======

  // Start (idempotente: si hay sesión pendiente, el backend la devuelve en vez de fallar)
  const handleStart = async () => {
    setStarting(true);
    setError('');
    setInfoMsg('');
    setResult(null);
    setPayout(null);
    setWinnings(null);
    setDealerLow([]);
    setDealerHigh([]);
    try {
      const data = await startPaiGow(Number(betAmount));
      // data:
      // { pending:true, betAmount, playerHand, dealerHand }
      // { pending:false, playerHand, dealerHand, newBalance }
      const p7 = Array.isArray(data.playerHand) ? data.playerHand : [];
      const d7 = Array.isArray(data.dealerHand) ? data.dealerHand : [];
      setPlayer7(p7);
      setDealer7(d7);
      setAssign(Object.fromEntries(p7.map(c => [c.card, 'none'])));

      if (typeof data.betAmount === 'number') {
        setBetAmount(Number(data.betAmount));
      }
      if (typeof data.newBalance === 'number') {
        setBalance(Number(data.newBalance));
      }
      setPending(Boolean(data.pending));
      if (data.pending) {
        setInfoMsg(
          t('paiGow.pending_resumed', { defaultValue: 'Pending round resumed. Arrange your Low (2) and High (5).' })
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('paiGow.error_start', { defaultValue: 'Error starting Pai Gow Poker' });
      setError(msg);
    } finally {
      setStarting(false);
    }
  };

  // Resolve
  const handleResolve = async () => {
    if (!canResolve) {
      setError(
        t('paiGow.must_complete', {
          defaultValue: 'Complete your Low (2) and High (5) hands before resolving.',
        })
      );
      return;
    }
    setResolving(true);
    setError('');
    setInfoMsg('');
    try {
      const data = await resolvePaiGow(lowHand, highHand);
      // { result, player:{low,high}, dealer:{low,high}, payout, winnings, newBalance }
      if (data) {
        if (typeof data.newBalance === 'number') setBalance(data.newBalance);
        if (Array.isArray(data.dealer?.low)) setDealerLow(data.dealer.low);
        if (Array.isArray(data.dealer?.high)) setDealerHigh(data.dealer.high);
        setResult(data.result || null);
        setPayout(typeof data.payout === 'number' ? data.payout : null);
        setWinnings(typeof data.winnings === 'number' ? data.winnings : null);
        setPending(false);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('paiGow.error_resolve', { defaultValue: 'Error resolving Pai Gow Poker' });
      setError(msg);
    } finally {
      setResolving(false);
    }
  };

  // Cancelar (reembolsa y limpia sesión)
  const handleCancel = async () => {
    setCanceling(true);
    setError('');
    setInfoMsg('');
    try {
      const out = await cancelPaiGow();
      if (out?.refunded) {
        setInfoMsg(t('paiGow.refunded', { defaultValue: 'Round cancelled and ante refunded.' }));
      } else {
        setInfoMsg(t('paiGow.nothing_to_cancel', { defaultValue: 'No pending round to cancel.' }));
      }
      // Limpiar UI
      resetRound();
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('paiGow.error_cancel', { defaultValue: 'Error cancelling Pai Gow round' });
      setError(msg);
    } finally {
      setCanceling(false);
    }
  };

  // Historial
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    setError('');
    setInfoMsg('');
    try {
      const rows = await getPaiGowHistory();
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('paiGow.error_history', { defaultValue: 'Error fetching history' });
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ====== Render ======
  const ResultBadge = () => {
    if (!result) return null;
    const color = { win: 'success', tie: 'warning', lose: 'danger' }[result] || 'secondary';
    const text = t(`paiGow.result.${result}`, {
      defaultValue: result === 'win' ? 'You win!' : result === 'lose' ? 'You lose' : 'Tie',
    });
    return <span className={`badge bg-${color}`}>{text}</span>;
  };

  const renderDealerSeven = () => (
    <div className="pgp-cards">
      {dealer7.map((_, idx) => (
        <div key={idx} className="pgp-card-back" aria-hidden />
      ))}
    </div>
  );

  const renderCardsRow = (codes, onClickHint) => (
    <div className="pgp-cards">
      {codes.map((code) => (
        <OverlayTrigger
          key={code}
          placement="top"
          overlay={<Tooltip>{onClickHint}</Tooltip>}
        >
          <img
            className={`pgp-card-img ${assign[code] || 'none'}`}
            src={getCardImage(code)}
            alt={code}
            onClick={() => cycleAssign(code)}
          />
        </OverlayTrigger>
      ))}
    </div>
  );

  const allCodes = player7.map((c) => c.card);
  const unassignedCodes = allCodes.filter((c) => (assign[c] || 'none') === 'none');

  return (
    <div className="pgp-container d-flex justify-content-center align-items-start">
      <Card className="pgp-card shadow-lg">
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              title={t('common.back', { defaultValue: 'Back' })}
            >
              <FaArrowLeft className="me-2" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>

            <h2 className="m-0">
              <FaCrown className="text-warning me-2" />
              {t('paiGow.title', { defaultValue: 'Pai Gow Poker' })}
              <FaCrown className="text-warning ms-2" />
            </h2>

            <div className="d-flex gap-2">
              <Button variant="outline-info" onClick={openHistory}>
                <FaHistory className="me-2" />
                {t('paiGow.history', { defaultValue: 'History' })}
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowRules(true)}>
                <FaInfoCircle className="me-2" />
                {t('paiGow.rules', { defaultValue: 'Rules' })}
              </Button>
            </div>
          </div>

          {/* Toolbar: Apuesta / Start / Cancel / Reset / Balance */}
          <div className="pgp-toolbar">
            <InputGroup style={{ maxWidth: 260 }}>
              <InputGroup.Text>{t('paiGow.bet', { defaultValue: 'Bet' })}</InputGroup.Text>
              <FormControl
                type="number"
                min={1}
                max={10000}
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                disabled={inRound || pending}
              />
              <InputGroup.Text>{t('paiGow.currency', { defaultValue: 'MENEL' })}</InputGroup.Text>
            </InputGroup>

            <div className="d-flex gap-2">
              <Button onClick={handleStart} disabled={starting || inRound}>
                {starting ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    {t('paiGow.start', { defaultValue: 'Start' })}
                  </>
                )}
              </Button>

              <Button
                variant="outline-secondary"
                onClick={resetRound}
                disabled={starting || (!inRound && !roundResolved && !pending)}
                title={t('paiGow.reset', { defaultValue: 'Reset' })}
              >
                <FaRedo className="me-2" />
                {t('paiGow.reset', { defaultValue: 'Reset' })}
              </Button>

              <Button
                variant="outline-danger"
                onClick={handleCancel}
                disabled={canceling || (!inRound && !pending)}
                title={t('paiGow.cancel', { defaultValue: 'Cancel' })}
              >
                {canceling ? <Spinner size="sm" /> : <FaBan className="me-2" />}
                {t('paiGow.cancel', { defaultValue: 'Cancel' })}
              </Button>
            </div>

            <div className="pgp-balance">
              {t('paiGow.balance', { defaultValue: 'Balance' })}:{' '}
              <strong>{balance ?? '—'}</strong>{' '}
              {t('paiGow.currency', { defaultValue: 'MENEL' })}
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
          {infoMsg && (
            <Alert variant="info" className="mt-3">
              {infoMsg}
            </Alert>
          )}

          {/* Pool + Zonas */}
          {player7.length > 0 && (
            <>
              <Row className="mt-3 g-3">
                <Col md={7}>
                  <div className="pgp-zone">
                    <div className="pgp-zone-header">
                      {t('paiGow.your_seven', { defaultValue: 'Your 7 cards' })}
                      <small className="text-muted ms-2">
                        {t('paiGow.click_to_assign', {
                          defaultValue: 'Click a card to cycle: None → Low → High',
                        })}
                      </small>
                    </div>
                    {renderCardsRow(
                      allCodes,
                      t('paiGow.click_to_assign', { defaultValue: 'Click to cycle assignment' })
                    )}
                    <div className="d-flex justify-content-end mt-2">
                      <Button size="sm" variant="outline-secondary" onClick={clearAssignments} disabled={roundResolved}>
                        {t('paiGow.clear', { defaultValue: 'Clear assignments' })}
                      </Button>
                    </div>
                  </div>
                </Col>

                <Col md={5}>
                  <div className="pgp-zone">
                    <div className="pgp-zone-header d-flex justify-content-between">
                      <span>{t('paiGow.dealer', { defaultValue: 'Dealer' })}</span>
                      {!roundResolved && (
                        <span className="text-muted">
                          {t('paiGow.hidden_until_resolve', { defaultValue: 'Hidden until resolve' })}
                        </span>
                      )}
                    </div>

                    {!roundResolved ? (
                      renderDealerSeven()
                    ) : (
                      <>
                        <div className="pgp-subtitle mb-1">{t('paiGow.dealer_low', { defaultValue: 'Dealer Low (2)' })}</div>
                        <div className="pgp-cards">
                          {dealerLow.map((code) => (
                            <img key={code} className="pgp-card-img static" src={getCardImage(code)} alt={code} />
                          ))}
                        </div>
                        <div className="pgp-subtitle mt-2 mb-1">{t('paiGow.dealer_high', { defaultValue: 'Dealer High (5)' })}</div>
                        <div className="pgp-cards">
                          {dealerHigh.map((code) => (
                            <img key={code} className="pgp-card-img static" src={getCardImage(code)} alt={code} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mt-3 g-3">
                <Col md={6}>
                  <div className="pgp-zone">
                    <div className="pgp-zone-header">
                      {t('paiGow.low_hand', { defaultValue: 'Your Low (2)' })}{' '}
                      <small className={lowHand.length === 2 ? 'text-success' : 'text-muted'}>
                        ({lowHand.length}/2)
                      </small>
                    </div>
                    <div className="pgp-cards">
                      {lowHand.map((code) => (
                        <img
                          key={code}
                          className="pgp-card-img low"
                          src={getCardImage(code)}
                          alt={code}
                          onClick={() => cycleAssign(code)}
                          title={t('paiGow.click_to_cycle', { defaultValue: 'Click to move to High/None' })}
                        />
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="pgp-zone">
                    <div className="pgp-zone-header">
                      {t('paiGow.high_hand', { defaultValue: 'Your High (5)' })}{' '}
                      <small className={highHand.length === 5 ? 'text-success' : 'text-muted'}>
                        ({highHand.length}/5)
                      </small>
                    </div>
                    <div className="pgp-cards">
                      {highHand.map((code) => (
                        <img
                          key={code}
                          className="pgp-card-img high"
                          src={getCardImage(code)}
                          alt={code}
                          onClick={() => cycleAssign(code)}
                          title={t('paiGow.click_to_cycle', { defaultValue: 'Click to move to Low/None' })}
                        />
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>

              {!roundResolved && (
                <div className="d-flex justify-content-center mt-4">
                  <Button variant="success" onClick={handleResolve} disabled={!canResolve || resolving}>
                    {resolving ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <FaCheck className="me-2" />
                        {t('paiGow.resolve', { defaultValue: 'Resolve' })}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Resultado */}
          {roundResolved && (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <ResultBadge />
              </div>
              <div>
                {t('paiGow.payout', { defaultValue: 'Payout' })}: <strong>{payout ?? 0}</strong>{' '}
                {t('paiGow.currency', { defaultValue: 'MENEL' })}
              </div>
              <div>
                {t('paiGow.winnings', { defaultValue: 'Winnings' })}: <strong>{winnings ?? 0}</strong>{' '}
                {t('paiGow.currency', { defaultValue: 'MENEL' })}
              </div>
              <div>
                {t('paiGow.new_balance', { defaultValue: 'New balance' })}:{' '}
                <strong>{balance ?? '—'}</strong> {t('paiGow.currency', { defaultValue: 'MENEL' })}
              </div>

              <div className="d-flex gap-2 mt-3">
                <Button onClick={handleStart} disabled={starting || inRound}>
                  {starting ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FaPlay className="me-2" />
                      {t('paiGow.play_again', { defaultValue: 'Play again' })}
                    </>
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={resetRound}>
                  <FaRedo className="me-2" />
                  {t('paiGow.reset', { defaultValue: 'Reset' })}
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal: Reglas */}
      <Modal show={showRules} onHide={() => setShowRules(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            {t('paiGow.rules', { defaultValue: 'Rules' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="mb-0">
            <li>
              <strong>{t('paiGow.rule_ante', { defaultValue: 'Ante' })}:</strong>{' '}
              {t('paiGow.rule_ante_text', {
                defaultValue:
                  'Pay the Ante to receive 7 cards. The dealer also draws 7 cards.',
              })}
            </li>
            <li>
              <strong>{t('paiGow.rule_split', { defaultValue: 'Split' })}:</strong>{' '}
              {t('paiGow.rule_split_text', {
                defaultValue:
                  'Arrange your hand into a Low (2 cards) and a High (5 cards).',
              })}
            </li>
            <li>
              <strong>{t('paiGow.rule_showdown', { defaultValue: 'Showdown' })}:</strong>{' '}
              {t('paiGow.rule_showdown_text', {
                defaultValue:
                  'On resolve, your Low vs dealer Low and your High vs dealer High decide the result.',
              })}
            </li>
            <li>
              <strong>{t('paiGow.rule_payouts', { defaultValue: 'Payouts' })}:</strong>{' '}
              {t('paiGow.rule_payouts_text', {
                defaultValue:
                  'Win: returns 2× bet (profit 1×). Tie: returns bet. Lose: lose Ante.',
              })}
            </li>
          </ul>
        </Modal.Body>
      </Modal>

      {/* Modal: Historial */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className="me-2" />
            {t('paiGow.history', { defaultValue: 'History' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {historyLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner />
            </div>
          ) : history.length ? (
            <ul className="list-group">
              {history.map((h, idx) => (
                <li key={idx} className="list-group-item">
                  <div>
                    <strong>{t('paiGow.result_label', { defaultValue: 'Result' })}:</strong> {h.result}
                  </div>
                  {'score' in h && (
                    <div>
                      <strong>{t('paiGow.score', { defaultValue: 'Score' })}:</strong> {h.score}
                    </div>
                  )}
                  <div>
                    <strong>{t('paiGow.details', { defaultValue: 'Details' })}:</strong> {h.details}
                  </div>
                  <div>
                    <strong>{t('paiGow.date', { defaultValue: 'Date' })}:</strong>{' '}
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">
              {t('paiGow.no_history', { defaultValue: 'No records yet.' })}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PaiGowPoker;
